import { useEffect } from 'react';
import { PageHeader } from '@/components/organisms/PageHeader';
import { logPageView } from '@/services/analytics.service';

export const PrivacyPage = () => {

  useEffect(() => {
    logPageView('Privacy Policy Page');
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader title="Privacy Policy" showBackButton />

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-4 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <div className="mb-8">
            <p className="text-muted-foreground text-sm">
              Last updated: {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              tuval.space ("we", "our", or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use, disclose,
              and safeguard your information when you use our collaborative pixel
              art platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold mb-3 mt-4">2.1 Information You Provide</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Account information (email address, username)</li>
              <li>Authentication data through Firebase Authentication</li>
              <li>Content you create (pixel placements, boards, sketches)</li>
              <li>Profile information and preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.2 Automatically Collected Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We automatically collect certain information when you use our service:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Usage data and analytics</li>
              <li>Device information and browser type</li>
              <li>IP address and location data (general)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and manage your account</li>
              <li>Send you technical notices and support messages</li>
              <li>Monitor and analyze usage patterns</li>
              <li>Detect, prevent, and address technical issues</li>
              <li>Enforce our terms of service and community guidelines</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Data Storage and Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Your data is stored securely using Firebase services. We implement
              appropriate technical and organizational measures to protect your
              personal information. However, no method of transmission over the
              Internet or electronic storage is 100% secure.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use Firebase Authentication for secure user authentication and
              Firestore for data storage. All data is encrypted in transit and at
              rest.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Information Sharing</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third
              parties. We may share your information only in the following
              circumstances:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>With your consent</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and safety</li>
              <li>With service providers who assist in operating our platform</li>
              <li>Public information (username, public boards) is visible to all users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Public Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Certain information on tuval.space is publicly visible:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Your username and public profile</li>
              <li>Boards you create (unless set to private)</li>
              <li>Pixel placements and modifications</li>
              <li>Public activity and statistics</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Please be aware that any information you make public can be viewed,
              collected, and used by others.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track activity on
              our service and hold certain information. You can instruct your
              browser to refuse all cookies or to indicate when a cookie is being
              sent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your account and data</li>
              <li>Opt-out of certain data collection</li>
              <li>Export your data</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To exercise these rights, please contact us through the platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our service is not intended for children under the age of 13. We do
              not knowingly collect personal information from children under 13. If
              you are a parent or guardian and believe your child has provided us
              with personal information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may update our Privacy Policy from time to time. We will notify
              you of any changes by posting the new Privacy Policy on this page
              and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact
              us through our platform or visit our GitHub repository.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

